import React, { useContext, useEffect, useMemo, useState } from 'react';

import { dashboardArrivalNoticeHooks } from '@apis/next/arrivalNoticeApis';
import { dashboardBookingInquiryApis } from '@apis/next/bookingInquiryApis';
import { dashboardPaymentSlipApis } from '@apis/next/paymentSlipApis';
import { dashboardPdfApis } from '@apis/next/pdfApis';
import lockIcon from '@assets/img/icons/lock.svg';
import unlockIcon from '@assets/img/icons/lock.svg';
import { ArrivalNoticePaymentInfoDisplay } from '@components/admin/booking/ArrivalNoticePaymentInfoDisplay';
import {
  ArrivalNoticeContext,
  ArrivalNoticeContextValues,
  BookingDetailsContext,
} from '@components/admin/booking/helpers';
import { PaymentSlipFormDialog } from '@components/admin/booking/PaymentSlipFormDialog';
import { Alert } from '@components/Alert';
import { Button } from '@components/Button';
import {
  ActionableDialog,
  ActionableDialogActions,
  ActionableDialogContentDivider,
  ActionableDialogTitle,
} from '@components/Dialog';
import { FormAlert } from '@components/Forms/FormAlert';
import {
  BookingFormContainer,
  BookingFormInput,
  BookingFormInputIndex,
  BookingFormInputLabel,
} from '@components/NextForms/BookingForm';
import { IndexedFormContainer, IndexedLabel } from '@components/NextForms/IndexedForm';
import { TextInput } from '@components/NextForms/TextInput';
import { QueryContainer } from '@components/QueryContainer';
import { TextBoxOptionGroup } from '@components/TextBox';
import { arrivalNoticeStrings, deliveryDestinationOptions } from '@data/arrivalNotice';
import { useConfirmDialog } from '@hooks/useActionableDialog';
import { useBlobURL } from '@hooks/useBlobURL';
import { useDialogKey } from '@hooks/useDialogKey';
import { useFormUtils } from '@hooks/useFormUtils';
import { useMemoVersion } from '@hooks/useMemoVersion';
import { AdminInquiryDetailHelper } from '@models/BookingInquiry';
import {
  ArrivalNoticeHelper,
  ArrivalNoticeForm as ArrivalNoticeFormSchema,
  // exampleArrivalNoticePaymentInfo,
} from '@models/next/ArrivalNotice';
import { decodeResultEqual, mapDecodeResult } from '@monoid-dev/mobx-zod-form';
import { ReactForm, useForm } from '@monoid-dev/mobx-zod-form-react';
import { makeMuiStyles, useCommonStyles } from '@styles';
import { useQueryClient } from '@tanstack/react-query';
import { foldMessagesEither } from '@utils/errors';
import { formStrings } from '@utils/forms';
import { createStrings } from '@utils/local';
import { url } from '@utils/pages';
import { diffPartial, id, mapOptionalOr, stringifySearch } from '@utils/types';
import clsx from 'clsx';
import { observer } from 'mobx-react';
import { Document, Page } from 'react-pdf';
import { omit, pipe } from 'remeda';

const strings = createStrings({
  ja: {
    createArrivalNotice: 'A/Nを作成する',
    arrivalNoticePreviewTitle: 'Arrival Noticeを作成する',
    preview: 'プレビュー',
    goBack: '戻る',
    create: '作成',
    export: 'エクスポート',
    changeLog: '変更履歴',
    fixPrompt: '記録が不完全のために、Arrival Noticeが作成できません。',
    detail: '変更理由',
    lock: 'A/N Lock',
    unlock: 'A/N Unlock',
    createANPaymentSlip: 'A/N支払伝票を作成',
    alreadyCreatedANPaymentSlip: 'A/N支払伝票は作成済みです。',
  },
});

const useStyles = makeMuiStyles((_theme, common) => ({
  formContainer: {
    padding: '1rem',
  },
  buttonContainer: {
    ...common.flexInline,
    gridColumn: '1 / -1',
    columnGap: '1rem',
    marginTop: '0.5rem',
    paddingLeft: 40,
    paddingRight: '2.2rem',
  },
  paymentInfoContainer: {
    ...common.flexColumn,
    gridColumn: '1 / -1',
    rowGap: '1rem',
    paddingLeft: 40,
    paddingRight: '2.2rem',
  },
  dialog: {
    maxWidth: 'initial !important',
    width: 960,
  },
  arrivalNoticePreviewContainer: {
    ...common.flexColumn,
    ...common.itemsCenter,
    height: 550,
    overflow: 'auto',
  },
  dialogContent: {
    ...common.flexColumn,
    rowGap: '1rem',
    padding: '1rem 2rem',
    flexShrink: 1,
    minHeight: 0,
    maxHeight: 550,
  },
}));

export const ArrivalNoticeForm: React.FC = () => {
  const { bookingInquiry, preAlert } = useContext(BookingDetailsContext);

  const arrivalNoticeQuery = dashboardArrivalNoticeHooks.useGet('/dashboard/arrival-notice/:id', {
    params: {
      id: bookingInquiry.id,
    },
  });

  const paymentSlipQuery = dashboardPaymentSlipApis.useGet('/dashboard/payment-slip', {
    queries: {
      d2d_no: bookingInquiry.d2d_no,
    },
  });

  const isPaymentSlipCreated = useMemo(
    () => paymentSlipQuery.data?.results.some((p) => p.arrival_notice_tag)!,
    [paymentSlipQuery.data],
  );

  const formKey = useMemoVersion(arrivalNoticeQuery.data);

  const arrivalNoticeContext: ArrivalNoticeContextValues = useMemo(
    () => ({
      preAlert,
      bookingInquiry,
      arrivalNotice: arrivalNoticeQuery.data!,
      isPaymentSlipCreated,
    }),
    [bookingInquiry, preAlert, arrivalNoticeQuery.data, isPaymentSlipCreated],
  );

  const classes = {
    ...useCommonStyles(),
    ...useStyles(),
  };

  const canCreate = AdminInquiryDetailHelper.canCreateArrivalNotice(preAlert);

  return (
    <ArrivalNoticeContext.Provider value={arrivalNoticeContext}>
      <QueryContainer queries={[arrivalNoticeQuery, paymentSlipQuery]}>
        <div className={classes.formContainer}>
          {mapOptionalOr(
            arrivalNoticeQuery.data,
            () => (
              <ArrivalNoticeFormDisplayEdit key={formKey} />
            ),
            () =>
              canCreate._tag === 'Right' ? (
                <ArrivalNoticeFormCreate />
              ) : (
                <div className={clsx(classes.middleText, classes.textGray600)}>
                  <div>{strings.fixPrompt}</div>
                  <ul>
                    {canCreate.left.messages.map((m) => (
                      <li key={m}>{m}</li>
                    ))}
                  </ul>
                </div>
              ),
          )}
        </div>
      </QueryContainer>
    </ArrivalNoticeContext.Provider>
  );
};

const ArrivalNoticeFormCreate: React.FC = () => {
  const { bookingInquiry, user, preAlert } = useContext(BookingDetailsContext);

  const queryClient = useQueryClient();

  const { confirm, renderDialog } = useConfirmDialog();

  const mutation = dashboardArrivalNoticeHooks.usePost('/dashboard/arrival-notice', undefined, {
    onSuccess() {
      queryClient.invalidateQueries(
        dashboardArrivalNoticeHooks.getKeyByPath('get', '/dashboard/arrival-notice/:id'),
      );
    },
  });

  return (
    <>
      <FormAlert mutation={mutation} gutter />
      {renderDialog()}

      <Button
        type="button"
        color="primary"
        size="small"
        shape="round"
        text={strings.createArrivalNotice}
        onClick={() =>
          pipe(new AdminInquiryDetailHelper(bookingInquiry).canEdit(user, preAlert), (e) => {
            if (e._tag === 'Right') {
              mutation.mutateAsync({ booking_inquiry: bookingInquiry.id });
            } else {
              confirm({ message: e.left.messages.join('\n') });
            }
          })
        }
        isLoading={mutation.isLoading}
      />
    </>
  );
};

const deliveryDestionationOptions = (() => {
  const options: TextBoxOptionGroup[] = [];
  for (const d of deliveryDestinationOptions) {
    const targetGroup = options.find((o) => o.label === d.port_of_discharge);
    if (targetGroup) {
      targetGroup.children.push({
        label: d.delivery_destination,
        value: d.delivery_destination,
      });
    } else {
      options.push({
        label: d.port_of_discharge,
        children: [
          {
            label: d.delivery_destination,
            value: d.delivery_destination,
          },
        ],
      });
    }
  }
  return options;
})();

const ArrivalNoticePreviewDialog: React.FC<{
  open: boolean;
  onClose: () => void;
  onSubmit: () => void;
  item: ArrivalNoticeFormSchema | null;
}> = (props) => {
  const { open, onClose, onSubmit, item } = props;

  const classes = {
    ...useCommonStyles(),
    ...useStyles(),
  };

  const { preAlert } = useContext(ArrivalNoticeContext);

  const pdfPreview = dashboardPdfApis.usePost('/dashboard/pdf/preview/ArrivalNotice');

  const pdfUrl = useBlobURL(pdfPreview.data);

  useEffect(() => {
    (async () => {
      if (open && item) {
        await pdfPreview.mutateAsync({
          ...item,
          gross_weight: String(item.gross_weight),
          measurement: String(item.measurement),
          d2d_taxable_amount: String(item.d2d_taxable_amount),
          payments_terms: preAlert.payments_terms,
          overseas_local_amount: preAlert.overseas_local_amount || '',
          exchange_rate: preAlert.exchange_rate,
        });
      }
    })();
  }, [open, preAlert, item]);

  const { renderAlertsAndSpinnerModal } = useFormUtils({ mutations: [pdfPreview] });

  return (
    <ActionableDialog open={open} onClose={onClose} size="lg">
      <ActionableDialogTitle textAlign="center" hideCloseButton>
        {strings.arrivalNoticePreviewTitle}
      </ActionableDialogTitle>

      <div className={classes.arrivalNoticePreviewContainer}>
        {renderAlertsAndSpinnerModal()}

        {pdfUrl && (
          <Document file={pdfUrl} loading="">
            <Page pageNumber={1} width={900} />
          </Document>
        )}
      </div>

      <ActionableDialogContentDivider />

      <ActionableDialogActions justifyContent="center">
        <Button
          color="primary"
          size="small"
          shape="round"
          type="button"
          text={strings.goBack}
          onClick={() => onClose()}
        />
        <Button
          color="primary"
          size="small"
          shape="round"
          type="button"
          text={strings.create}
          onClick={() => onSubmit()}
        />
      </ActionableDialogActions>
    </ActionableDialog>
  );
};

const DeliveryDestionationInput: React.FC<{
  form: ReactForm<typeof ArrivalNoticeFormSchema>;
}> = observer(({ form }) => {
  const {
    root: { fields },
  } = form;

  const isOther = decodeResultEqual(fields.delivery_destination.decodeResult, 'その他');

  return (
    <>
      <BookingFormInput
        index={17}
        field={fields.delivery_destination}
        inputOptions={{ type: 'choose', options: deliveryDestionationOptions }}
        editDialogElement={<EditDeliveryDestionationDialogContent form={form} />}
      />
      {isOther && (
        <>
          <BookingFormInput field={fields.repository} canEdit={false} />
          <BookingFormInput field={fields.address} canEdit={false} />
          <BookingFormInput field={fields.tel} canEdit={false} />
          <BookingFormInput field={fields.fax} canEdit={false} />
          <BookingFormInput field={fields.principal_name} canEdit={false} />
        </>
      )}
    </>
  );
});

const EditDeliveryDestionationDialogContent: React.FC<{
  form: ReactForm<typeof ArrivalNoticeFormSchema>;
}> = observer(({ form }) => {
  const classes = {
    ...useCommonStyles(),
    ...useStyles(),
  };

  const {
    root: { fields },
  } = form;

  const isOther = decodeResultEqual(fields.delivery_destination.decodeResult, 'その他');

  pipe(mapDecodeResult(fields.delivery_destination.decodeResult, id, ''), (delivery_destination) =>
    useEffect(() => {
      const resolved = deliveryDestinationOptions.find(
        (option) => option.delivery_destination === delivery_destination,
      );

      fields.delivery_destination_code.setOutput(resolved?.delivery_destination_code ?? '');
    }, [delivery_destination]),
  );

  return (
    <>
      <ActionableDialogTitle>
        {fields.delivery_destination.type.getFormMeta().label}
      </ActionableDialogTitle>

      <div className={classes.dialogContent}>
        <IndexedFormContainer size="md">
          <IndexedLabel field={fields.delivery_destination} />
          <div>
            <TextInput
              field={fields.delivery_destination}
              inputOptions={{ type: 'choose', options: deliveryDestionationOptions }}
            />
          </div>

          {isOther && (
            <>
              <IndexedLabel field={fields.repository} />
              <div>
                <TextInput field={fields.repository} />
              </div>
              <IndexedLabel field={fields.address} />
              <div>
                <TextInput field={fields.address} />
              </div>
              <IndexedLabel field={fields.tel} />
              <div>
                <TextInput field={fields.tel} />
              </div>
              <IndexedLabel field={fields.fax} />
              <div>
                <TextInput field={fields.fax} />
              </div>

              <IndexedLabel field={fields.principal_name} />
              <div>
                <TextInput field={fields.principal_name} />
              </div>
            </>
          )}
        </IndexedFormContainer>
      </div>
    </>
  );
});

const ArrivalNoticeFormDisplayEdit: React.FC = () => {
  const { arrivalNotice, isPaymentSlipCreated } = useContext(ArrivalNoticeContext);
  const { bookingInquiry, preAlert, user } = useContext(BookingDetailsContext);

  const [paymentSlipDialogOpen, setPaymentSlipDialogOpen] = useState(false);

  const queryClient = useQueryClient();

  const refresh = () => {
    queryClient.invalidateQueries(
      dashboardArrivalNoticeHooks.getKeyByPath('get', '/dashboard/arrival-notice/:id'),
    );
    queryClient.invalidateQueries(
      dashboardPaymentSlipApis.getKeyByPath('get', '/dashboard/payment-slip'),
    );
    queryClient.invalidateQueries(
      dashboardBookingInquiryApis.getKeyByPath(
        'get',
        '/dashboard/booking-inquiry/:d2d_no/status-flows',
      ),
    );
  };

  const classes = {
    ...useCommonStyles(),
    ...useStyles(),
  };

  const initialOutput = useMemo(
    () => ArrivalNoticeHelper.mapToForm(arrivalNotice),
    [arrivalNotice],
  );

  const [previewArrivalNotice, setPreviewArrivalNotice] = useState<ArrivalNoticeFormSchema | null>(
    null,
  );

  const form = useForm(ArrivalNoticeFormSchema, {
    initialOutput,
  });

  const {
    root: { fields },
  } = form;

  const isFormValid = useMemo(
    () => ArrivalNoticeHelper.isFormValid(arrivalNotice),
    [arrivalNotice],
  );

  const mode = isFormValid ? 'display' : 'edit';

  const putArrivalNoticeAction = dashboardArrivalNoticeHooks.usePut(
    '/dashboard/arrival-notice/:id',
    {
      params: {
        id: arrivalNotice.id,
      },
    },
    {
      onSuccess: refresh,
    },
  );

  const patchArrivalNoticeAction = dashboardArrivalNoticeHooks.usePatch(
    '/dashboard/arrival-notice/:id',
    {
      params: {
        id: arrivalNotice.id,
      },
    },
    {
      onSuccess: refresh,
    },
  );

  const { renderAlertsAndSpinnerModal } = useFormUtils({
    mutations: [putArrivalNoticeAction, patchArrivalNoticeAction],
  });

  const { confirm, renderDialog } = useConfirmDialog();

  return (
    <form
      {...form.bindForm({
        async onSubmit(values) {
          switch (values.action) {
            case 'preview':
              setPreviewArrivalNotice(values);
              return;
            case 'submit':
              putArrivalNoticeAction.reset();
              await putArrivalNoticeAction.mutateAsync(values);

              return;
            case 'edit':
              const diff = diffPartial(initialOutput, omit(values, ['action', 'detail']));

              if (Object.keys(diff).length > 0) {
                await patchArrivalNoticeAction.mutateAsync({
                  ...diff,
                  detail: values.detail,
                });
              }

              return;
          }
        },
      })}
    >
      <ArrivalNoticePreviewDialog
        open={!!previewArrivalNotice && decodeResultEqual(fields.action.decodeResult, 'preview')}
        onClose={() => {
          setPreviewArrivalNotice(null);
          queryClient.invalidateQueries(
            dashboardArrivalNoticeHooks.getKeyByPath('get', '/dashboard/arrival-notice/:id'),
          );
        }}
        onSubmit={() => {
          fields.action.setOutput('submit');
          form.element?.dispatchEvent(new Event('submit', { cancelable: true, bubbles: true }));
          setPreviewArrivalNotice(null);
        }}
        item={previewArrivalNotice}
      />

      <PaymentSlipFormDialog
        open={paymentSlipDialogOpen}
        key={useDialogKey(paymentSlipDialogOpen)}
        onClose={() => setPaymentSlipDialogOpen(false)}
        bookingInquiry={bookingInquiry}
        preAlert={preAlert}
        type="create"
        isArrivalNoticeTag
      />

      {renderDialog()}

      {renderAlertsAndSpinnerModal()}

      {!arrivalNotice.is_locked &&
        pipe(
          ArrivalNoticeHelper.canLockForConsistentDeliveryDestination(arrivalNotice),
          (e) =>
            e._tag === 'Left' && (
              <Alert severity="error" open gutter scrollOnOpen={form.submitCount > 0}>
                {e.left.messages}
              </Alert>
            ),
        )}

      {!!arrivalNotice.sea_delivery_destination_code &&
        initialOutput.sea_delivery_destination === '' && (
          <Alert severity="error" open gutter scrollOnOpen={form.submitCount > 0}>
            {arrivalNoticeStrings.errorSeaDeliveryDestinationNotFound}
          </Alert>
        )}

      <BookingFormContainer
        mode={mode}
        detailField={fields.detail}
        onEditClick={() => fields.action.setOutput('edit')}
        onCancelEdit={() => {
          form.root.setOutput(initialOutput);
        }}
        isLocked={arrivalNotice.is_locked}
      >
        <BookingFormInput
          index={1}
          field={fields.house_bl_number}
          inputOptions={{ type: 'display' }}
          canEdit={false}
        />
        <BookingFormInput index={2} field={fields.shipper} />
        <BookingFormInput index={3} field={fields.consignee} />
        <BookingFormInput index={4} field={fields.shipping_mark} />

        <BookingFormInput index={5} field={fields.vessel} disabled canEdit={false} />
        <BookingFormInput index={6} field={fields.voyage} disabled canEdit={false} />

        <BookingFormInput index={7} field={fields.pol} disabled canEdit={false} />
        <BookingFormInput index={8} field={fields.pod} disabled canEdit={false} />

        <BookingFormInput index={9} field={fields.etd} disabled canEdit={false} />
        <BookingFormInput index={10} field={fields.eta} disabled canEdit={false} />

        <BookingFormInput index={11} field={fields.commodity} />

        <BookingFormInput index={12} field={fields.nos} />
        <BookingFormInput index={13} field={fields.packages} />
        <BookingFormInput index={14} field={fields.gross_weight} />
        <BookingFormInput index={15} field={fields.measurement} />
        <BookingFormInput index={16} field={fields.d2d_taxable_amount} />

        <DeliveryDestionationInput form={form} />

        <BookingFormInput index={18} field={fields.sea_delivery_destination} canEdit={false} />

        <div className={classes.buttonContainer}>
          {mode === 'edit' && (
            <>
              <Button
                color="primary"
                size="small"
                shape="round"
                text={strings.preview}
                type="submit"
                onClick={() => fields.action.setOutput('preview')}
              />
              <Button
                color="primary"
                size="small"
                shape="round"
                text={formStrings.save}
                type="submit"
                onClick={() => fields.action.setOutput('submit')}
              />
            </>
          )}

          {mode === 'display' && (
            <>
              <Button
                color="primary"
                size="small"
                shape="round"
                type="button"
                text={strings.export}
                onClick={() => {
                  foldMessagesEither(
                    AdminInquiryDetailHelper.canExportArrivalNotice(preAlert),
                    ({ messages }) => {
                      confirm({ message: messages });
                    },
                    () => {
                      window.open(
                        `${url('/utilities/Bookings/ExportArrivalNotice')}?${stringifySearch({
                          d2dNo: arrivalNotice.house_bl_number,
                          isClient: false,
                        })}`,
                        '_blank',
                      );
                    },
                  );
                }}
              />
              <Button
                color="primary"
                size="small"
                shape="round"
                type="button"
                text={strings.changeLog}
                onClick={() =>
                  window.open(
                    `${url('/utilities/Bookings/PrintArrivalNoticeLogs')}?${stringifySearch({
                      id: arrivalNotice.id,
                      isClient: false,
                    })}`,
                    '_blank',
                  )
                }
              />
              <div style={{ flex: 1 }} />
              <Button
                text={
                  <>
                    {!arrivalNotice.is_locked && (
                      <>
                        <img src={lockIcon} alt="Lock" style={{ paddingRight: '0.2rem' }} />
                        {strings.lock}
                      </>
                    )}
                    {arrivalNotice.is_locked && (
                      <>
                        <img src={unlockIcon} alt="Unlock" style={{ paddingRight: '0.2rem' }} />
                        {strings.unlock}
                      </>
                    )}
                  </>
                }
                color={arrivalNotice.is_locked ? 'warning' : 'primary'}
                size="small"
                shape="round"
                type="button"
                onClick={async () => {
                  if (!arrivalNotice.is_locked) {
                    await pipe(
                      ArrivalNoticeHelper.canLock(user, bookingInquiry, preAlert, arrivalNotice),
                      (e) =>
                        e._tag === 'Left'
                          ? confirm({
                              message: e.left.messages.join('\n'),
                            })
                          : patchArrivalNoticeAction.mutateAsync({
                              is_locked: true,
                              detail: '',
                            }),
                    );
                  } else {
                    foldMessagesEither(
                      AdminInquiryDetailHelper.canUnlock(user, preAlert),
                      ({ messages }) => {
                        confirm({
                          messages,
                        });
                      },
                      () => {
                        confirm({
                          message: arrivalNoticeStrings.warningUnlock,
                        }).then((r) => {
                          r === 'confirmed' &&
                            patchArrivalNoticeAction.mutateAsync({
                              is_locked: false,
                              detail: '',
                            });
                        });
                      },
                    );
                  }
                }}
              />
            </>
          )}
        </div>

        {ArrivalNoticeHelper.shouldDisplayPaymentInfo(arrivalNotice) && (
          <>
            <BookingFormInputIndex index={19} />
            <BookingFormInputLabel label={arrivalNoticeStrings.payment_info} />
            <div />

            <div
              className={classes.paymentInfoContainer}
              aria-label={arrivalNoticeStrings.payment_info}
            >
              {arrivalNotice.payment_info &&
              arrivalNotice.payment_info.payment_details.length > 0 ? (
                <ArrivalNoticePaymentInfoDisplay paymentInfo={arrivalNotice.payment_info} />
              ) : (
                ArrivalNoticeHelper.shouldHavePaymentInfo(arrivalNotice) && (
                  <>
                    {!arrivalNotice.payment_info && (
                      <div className={classes.textGray300}>
                        {arrivalNoticeStrings.warningNoPaymentInfo}
                      </div>
                    )}
                    {arrivalNotice.payment_info &&
                      !arrivalNotice.payment_info.payment_details.length && (
                        <div className={classes.textGray300}>
                          {arrivalNoticeStrings.warningNoPaymentDetails}
                        </div>
                      )}
                  </>
                )
              )}
              <div>
                {!isPaymentSlipCreated ? (
                  <Button
                    color="accent"
                    shape="round"
                    size="small"
                    type="button"
                    text={strings.createANPaymentSlip}
                    onClick={() => setPaymentSlipDialogOpen(true)}
                  />
                ) : (
                  <Button
                    color="grey"
                    shape="round"
                    size="small"
                    type="button"
                    text={strings.createANPaymentSlip}
                    aria-disabled
                    onClick={() =>
                      confirm({
                        message: strings.alreadyCreatedANPaymentSlip,
                      })
                    }
                  />
                )}
              </div>
            </div>
          </>
        )}
      </BookingFormContainer>
    </form>
  );
};
